import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { isValidCustomerPhone } from '../../lib/domain';
import { Badge, ErrorState, Field, Spinner } from '../../components/UI';
import { Link, navigate } from '../../lib/router';

const date = (value, includeTime = false) =>
  value
    ? new Intl.DateTimeFormat('en-SG', {
        dateStyle: 'medium',
        ...(includeTime ? { timeStyle: 'short' } : {}),
      }).format(new Date(value))
    : '—';

const money = (cents) =>
  new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(
    (cents || 0) / 100,
  );

const discountLabel = (voucher) => voucher.discountType === 'amount'
  ? money(voucher.discountAmountCents)
  : `${voucher.discountPercent}%`;

function useLoad(loader, dependencies) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const load = () => {
    let live = true;
    setState({ loading: true, data: null, error: null });
    loader()
      .then((data) => live && setState({ loading: false, data, error: null }))
      .catch((error) => live && setState({ loading: false, data: null, error }));
    return () => {
      live = false;
    };
  };
  useEffect(load, dependencies); // eslint-disable-line react-hooks/exhaustive-deps
  return [state, load];
}

export function ClaimPage({ slug }) {
  const [state, retry] = useLoad(() => api.publicBusiness(slug), [slug]);
  const [form, setForm] = useState({ country: 'SG', phone: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  async function submit(event) {
    event.preventDefault();
    if (!isValidCustomerPhone(form.phone, form.country)) {
      setErrors({ phone: 'Enter a valid Singapore or Malaysia mobile number.' });
      setMessage('');
      return;
    }
    setBusy(true);
    setErrors({});
    setMessage('');
    try {
      const result = await api.claim(slug, { ...form, acknowledged: true });
      navigate(`/v/${result.token}`);
    } catch (error) {
      const fieldErrors = error.fields || {};
      setErrors(fieldErrors);
      setMessage(
        fieldErrors.phone
          ? ''
          : Object.keys(fieldErrors).length
            ? Object.values(fieldErrors)[0]
            : error.message,
      );
    } finally {
      setBusy(false);
    }
  }

  if (state.loading) {
    return (
      <main className="public-page centered">
        <Spinner label="Loading today’s voucher" />
      </main>
    );
  }
  if (state.error) {
    return (
      <main className="public-page centered">
        <ErrorState error={state.error} retry={retry} />
      </main>
    );
  }

  const { business, offer: voucherConfig } = state.data;
  return (
    <main className="public-page reference-claim-page">
      <section className="reference-claim-card">
        <img
          className="customer-product-logo customer-claim-logo"
          src="/logo-v2-wordmark.png"
          alt="LoyalQR.sg"
        />
        <header className="reference-business">
          <div className={`reference-business-mark ${business.logoUrl ? 'has-photo' : ''}`}>
            {business.logoUrl ? (
              <img src={business.logoUrl} alt="" />
            ) : (
              business.name.trim().charAt(0).toUpperCase()
            )}
          </div>
          <strong>{business.name}</strong>
        </header>
        {voucherConfig ? (
          <>
            <div className="reference-voucher-preview">
              <div className="reference-voucher-value">
                <strong>{discountLabel(voucherConfig)}</strong>
                <span>OFF</span>
              </div>
              <div className="reference-voucher-copy">
                <span className="reference-voucher-label">Voucher</span>
                <h1>{voucherConfig.title}</h1>
                {voucherConfig.description && <p>{voucherConfig.description}</p>}
                <div className="reference-voucher-code">
                  <span>Voucher code</span>
                  <code>{voucherConfig.code}</code>
                </div>
                <div className="reference-terms reference-voucher-terms">
                  <strong>Terms &amp; conditions</strong>
                  <p>
                    {voucherConfig.minimumSpendCents > 0 && <>Minimum spend {money(voucherConfig.minimumSpendCents)}. </>}
                    {voucherConfig.validityDays > 0 && <>Valid for {voucherConfig.validityDays} days. </>}
                    {voucherConfig.terms}
                  </p>
                </div>
              </div>
            </div>
            <p className="reference-entry-copy">
              Enter your mobile number to claim this voucher or open your saved vouchers.
            </p>
            <form className="reference-claim-form" onSubmit={submit}>
              {message && (
                  <div className="alert error" role="alert">
                    {message}
                  </div>
              )}
              <Field label="Mobile number" error={errors.phone}>
                <div className={`phone-field ${errors.phone ? 'has-error' : ''}`}>
                  <select
                      value={form.country}
                      onChange={(event) => {
                        setForm({...form, country: event.target.value});
                        setErrors({});
                        setMessage('');
                      }}
                      aria-label="Country"
                  >
                    <option value="SG">🇸🇬 +65</option>
                    <option value="MY">🇲🇾 +60</option>
                  </select>
                  <input
                      value={form.phone}
                      onChange={(event) => {
                        setForm({...form, phone: event.target.value});
                        setErrors({});
                        setMessage('');
                      }}
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder={form.country === 'SG' ? '9123 4567' : '12 345 6789'}
                      aria-invalid={Boolean(errors.phone)}
                      required
                  />
                </div>
              </Field>
              <p className="claim-privacy-note">
                We’ll link this voucher to your mobile number so you can access it later.
              </p>
              <button className="button primary wide reference-claim-button" disabled={busy}>
                <span>{busy ? 'Opening…' : 'View or claim voucher'}</span>
                {!busy && (
                    <svg viewBox="0 0 20 20" aria-hidden="true">
                      <path d="M4 10h11M11 6l4 4-4 4"/>
                    </svg>
                )}
              </button>
            </form>
          </>
        ) : (
            <div className="reference-empty">
              <h1>No voucher right now</h1>
              <p>Check back with {business.name} soon.</p>
          </div>
        )}
      </section>
      <footer className="public-powered">
        Powered by <a href="https://loyalqr.sg">LoyalQR.sg</a>
      </footer>
    </main>
  );
}

function CustomerVoucherCard({ voucher, past = false }) {
  const statusText = {
    pending: `Cannot redeem until ${date(voucher.eligibleAt)}`,
    ready: 'Redeemable now',
    redeemed: `Redeemed ${date(voucher.redeemedAt, true)}`,
    expired: 'Cannot redeem · Voucher expired',
  }[voucher.status];
  const statusClass = voucher.status === 'ready'
    ? 'can-redeem'
    : voucher.status === 'redeemed'
      ? 'is-complete'
      : 'cannot-redeem';
  return (
    <article className={`loyal-voucher-card customer-public-voucher ${past ? 'is-subdued' : ''}`}>
      <div className="loyal-voucher-value" aria-label={`${discountLabel(voucher)} off`}>
        <strong>{discountLabel(voucher)}</strong>
        <span>OFF</span>
      </div>
      <div className="loyal-voucher-body">
        <span className="customer-voucher-label">Digital voucher</span>
        <div className="loyal-voucher-heading">
          <strong>{voucher.title}</strong>
          <Badge status={voucher.status} />
        </div>
        <p>{voucher.description || 'A voucher for your next visit.'}</p>
        <div className="loyal-voucher-terms">
          <span className={`voucher-redemption-status ${statusClass}`}>
            {statusText}
          </span>
          {voucher.validityDays > 0 && <span>Expires {date(new Date(new Date(voucher.expiresAt) - 1))}</span>}
        </div>
        <div className="loyal-voucher-code">
          <span>Voucher code</span>
          <code>{voucher.code}</code>
        </div>
        <small className="loyal-voucher-note">
          {voucher.minimumSpendCents > 0 && <>Minimum spend {money(voucher.minimumSpendCents)}. </>}
          {voucher.terms}
        </small>
      </div>
    </article>
  );
}

function AvailableVoucherCard({ voucher, busy, error, onClaim }) {
  return (
    <article className="loyal-voucher-card customer-public-voucher customer-available-voucher">
      <div className="loyal-voucher-value" aria-label={`${discountLabel(voucher)} off`}>
        <strong>{discountLabel(voucher)}</strong>
        <span>OFF</span>
      </div>
      <div className="loyal-voucher-body">
        <span className="customer-voucher-label">Available voucher</span>
        <div className="loyal-voucher-heading">
          <strong>{voucher.title}</strong>
          <Badge status="unclaimed" />
        </div>
        <p>{voucher.description || 'A voucher for your next visit.'}</p>
        <small className="loyal-voucher-note">
          {voucher.minimumSpendCents > 0 && <>Minimum spend {money(voucher.minimumSpendCents)}. </>}
          {voucher.validityDays > 0 && <>Valid for {voucher.validityDays} days after claiming. </>}
          {voucher.terms}
        </small>
        <div className="customer-available-voucher-action">
          <button type="button" className="button primary" disabled={busy} onClick={onClaim}>
            {busy ? 'Claiming…' : 'Claim voucher'}
          </button>
          {error && <small className="customer-claim-error" role="alert">{error}</small>}
        </div>
      </div>
    </article>
  );
}

export function VoucherPage({ token }) {
  const [state, retry] = useLoad(async () => {
    const wallet = await api.voucher(token);
    const publicBusiness = await api.publicBusiness(wallet.business.slug);
    return { ...wallet, activeOffer: publicBusiness.offer };
  }, [token]);
  const [activeTab, setActiveTab] = useState('vouchers');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimError, setClaimError] = useState('');

  useEffect(() => {
    setFirstName(state.data?.customer?.firstName || '');
    setLastName(state.data?.customer?.lastName || '');
  }, [state.data?.customer?.firstName, state.data?.customer?.lastName]);

  if (state.loading) {
    return (
      <main className="public-page centered">
        <Spinner label="Opening your vouchers" />
      </main>
    );
  }
  if (state.error) {
    const invalidLink = state.error.status === 404 || state.error.status === 410;
    return (
      <main className="public-page centered">
        <div className="state">
          <strong>{invalidLink ? 'This voucher link is no longer available' : 'We can’t open your vouchers right now'}</strong>
          <p>
            {invalidLink
              ? 'Scan the business QR again and enter your mobile number to reopen your saved vouchers.'
              : 'Check your connection and try again. Your vouchers are still saved.'}
          </p>
          {!invalidLink && <button className="button secondary" onClick={retry}>Try again</button>}
        </div>
      </main>
    );
  }

  const { vouchers = [state.data.voucher], business, customer, campaign, activeOffer } = state.data;
  const ongoing = vouchers
    .filter((item) => item.status === 'pending' || item.status === 'ready')
    .sort((left, right) => (left.status === 'ready' ? -1 : 1) - (right.status === 'ready' ? -1 : 1));
  const past = vouchers.filter((item) => item.status !== 'pending' && item.status !== 'ready');
  const availableOffer = activeOffer && activeOffer.id !== campaign?.id ? activeOffer : null;
  const readyCount = ongoing.filter((item) => item.status === 'ready').length;

  async function claimAvailableVoucher() {
    const digits = customer.phone.replace(/\D/g, '');
    const country = digits.startsWith('60') ? 'MY' : 'SG';
    const phone = country === 'MY' ? digits.replace(/^60/, '') : digits.replace(/^65/, '');
    setClaimBusy(true);
    setClaimError('');
    try {
      const result = await api.claim(business.slug, { country, phone, acknowledged: true });
      navigate(`/v/${result.token}`);
    } catch (error) {
      setClaimError(error.message);
      setClaimBusy(false);
    }
  }

  async function saveProfile(event) {
    event.preventDefault();
    setProfileBusy(true);
    setProfileError('');
    setProfileMessage('');
    try {
      await api.updatePublicCustomer(token, { firstName, lastName });
      setProfileMessage(firstName.trim() || lastName.trim() ? 'Name saved.' : 'Name removed.');
    } catch (error) {
      setProfileError(error.fields?.firstName || error.fields?.lastName || error.message);
    } finally {
      setProfileBusy(false);
    }
  }

  return (
    <main className="public-page customer-wallet-page">
      <section className="customer-wallet-shell">
        <header className="customer-wallet-business">
          <div className="voucher-business">
            {business.logoUrl ? (
              <img src={business.logoUrl} alt="" />
            ) : (
              <span className="minimal-business-fallback">{business.name.trim().charAt(0).toUpperCase()}</span>
            )}
            <div>
              <p>{business.name}</p>
              <small>{business.outletName}</small>
            </div>
          </div>
          <img
            className="customer-product-logo customer-wallet-logo"
            src="/logo-v2-wordmark.png"
            alt="LoyalQR.sg"
          />
        </header>

        <nav className="customer-wallet-tabs" role="tablist" aria-label="Customer wallet">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'vouchers'}
            aria-controls="customer-vouchers-panel"
            onClick={() => setActiveTab('vouchers')}
          >
            Vouchers
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'details'}
            aria-controls="customer-details-panel"
            onClick={() => setActiveTab('details')}
          >
            Your details
          </button>
        </nav>

        {activeTab === 'vouchers' ? (
          <div id="customer-vouchers-panel" role="tabpanel">
            <div className="customer-wallet-intro">
              <h1>Your vouchers</h1>
              <p>{firstName.trim() ? `Welcome back, ${[firstName.trim(), lastName.trim()].filter(Boolean).join(' ')}.` : `Saved vouchers from ${business.name}.`}</p>
            </div>

            <aside className="customer-redemption-note">
              <strong>{readyCount ? `${readyCount} voucher${readyCount === 1 ? '' : 's'} ready to redeem` : 'How redemption works'}</strong>
              <span>{readyCount ? 'Show this screen and tell staff the mobile number used to claim.' : 'When a voucher is ready, show this screen to staff.'}</span>
            </aside>

            <section className="customer-wallet-group">
              <div className="customer-wallet-heading">
                <h2>Ongoing vouchers</h2>
              </div>
              {availableOffer || ongoing.length ? (
                <div className="customer-wallet-list">
                  {ongoing.map((item) => <CustomerVoucherCard key={item.id} voucher={item} />)}
                  {availableOffer && (
                    <AvailableVoucherCard
                      voucher={availableOffer}
                      busy={claimBusy}
                      error={claimError}
                      onClaim={claimAvailableVoucher}
                    />
                  )}
                </div>
              ) : (
                <p className="customer-wallet-empty">You have no ongoing vouchers.</p>
              )}
            </section>

            <section className="customer-wallet-group past-customer-vouchers">
              <div className="customer-wallet-heading">
                <h2>Past vouchers</h2>
              </div>
              {past.length ? (
                <div className="customer-wallet-list">
                  {past.map((item) => <CustomerVoucherCard key={item.id} voucher={item} past />)}
                </div>
              ) : (
                <p className="customer-wallet-empty">Redeemed and expired vouchers will appear here.</p>
              )}
            </section>
          </div>
        ) : (
          <section id="customer-details-panel" className="customer-details-panel" role="tabpanel">
            <div>
              <h1>Your details</h1>
              <p>Add your name if you would like {business.name} to recognise you.</p>
            </div>
            <div className="customer-detail-phone">
              <span>Mobile number</span>
              <strong>{customer.phone}</strong>
            </div>
            <form onSubmit={saveProfile}>
              <Field label="First name (optional)" error={profileError}>
                <input
                  value={firstName}
                  onChange={(event) => {
                    setFirstName(event.target.value);
                    setProfileMessage('');
                  }}
                  autoComplete="given-name"
                  maxLength="80"
                  placeholder="Your first name"
                />
              </Field>
              <Field label="Last name (optional)">
                <input
                  value={lastName}
                  onChange={(event) => {
                    setLastName(event.target.value);
                    setProfileMessage('');
                  }}
                  autoComplete="family-name"
                  maxLength="80"
                  placeholder="Your last name"
                />
              </Field>
              <button className="button primary" disabled={profileBusy}>
                {profileBusy ? 'Saving…' : 'Save details'}
              </button>
            </form>
            {profileMessage && <small className="customer-profile-success" role="status">{profileMessage}</small>}
          </section>
        )}
      </section>
      <footer className="public-powered">
        Powered by <a href="https://loyalqr.sg">LoyalQR.sg</a>
      </footer>
    </main>
  );
}

export function NotFoundPage() {
  return (
    <main className="public-page centered">
      <div className="state">
        <strong>Page not found</strong>
        <p>The page you requested does not exist.</p>
        <Link to="/" className="button primary">
          Back to LoyalQR
        </Link>
      </div>
    </main>
  );
}
